import { client, gql } from 'data-provider';
import { EPaymentType } from 'enums';
import { CustomUploadedFile, IExpense } from 'interfaces';
import { IExpenseImportRow } from 'utilities/expense-csv';

interface IAllExpensesQuery {
  startDate?: string;
  endDate?: string;
  searchString?: string;
  start?: number;
  limit?: number;
}

export const getAllExpenses = async (queryParams?: IAllExpensesQuery) => {
  const where = {
    query: queryParams && queryParams.searchString ? queryParams.searchString : '',
    startDate: queryParams && queryParams.startDate ? queryParams.startDate : undefined,
    endDate: queryParams && queryParams.endDate ? queryParams.endDate : undefined,
  };

  const query = gql`
    query ($where: JSON, $sort: SortInput, $start: Int, $limit: Int) {
      expenses(where: $where, sort: $sort, start: $start, limit: $limit) {
        data {
          id
          expenseDate
          amount
          note
          expensePhoto {
            avatar {
              uid
              name
              url
              size
            }
          }
          category {
            id
            name
          }
          salesExpense {
            id
            sales {
              id
              warehouse {
                name
              }
              client {
                name
              }
            }
          }

          purchaseExpense {
            id
            purchase {
              id
              warehouse {
                name
              }
              supplier {
                name
              }
            }
          }
          returnExpense {
            id
            return {
              id
              warehouse {
                name
              }
              user {
                name
              }
            }
          }
          damageExpense {
            id
            damage {
              id
              warehouse {
                name
              }
              user {
                name
              }
            }
          }
          transferStockExpense {
            id
            transferStock {
              id
              fromWarehouse {
                name
              }
              createdBy {
                name
              }
            }
          }
          paymentType
          paymentMethod {
            id
            bankName
          }
        }
        total
        size
        page
      }
    }
  `;

  const variables = {
    where,
    start: queryParams && queryParams.startDate ? queryParams.start : 1,
    limit: queryParams && queryParams.limit ? queryParams.limit : 10,
  };
  const data = await client.request(query, variables);
  return data.expenses as {
    data: IExpense[];
    page: number;
    size: number;
    total: number;
  };
};

export const getExpense = async (expenseId: string) => {
  const query = gql`
    query ($expenseId: ID!) {
      expense(id: $expenseId) {
        id
        expenseDate
        amount
        note
        paymentType
        category {
          id
          name
          description
        }
      }
    }
  `;

  const variables = { expenseId };
  const data = await client.request(query, variables);
  return data.expense;
};

export interface ICreateExpenseReturnType {
  id: string;
  category: { id: string; name: string };
  amount: number;
  paymentMethod: { bankName: string };
}

export interface ICreateExpenseParam {
  attachments: CustomUploadedFile[];
  amount: number;
  expenseDate: Date;
  category: string;
  paymentType: EPaymentType;
  paymentMethod: string;
  note: string;
  purchase?: string;
  sales?: string;
  return?: string;
  damage?: string;
}

export const createExpense = async (data: ICreateExpenseParam) => {
  const createExpense = gql`
    mutation ($input: createExpenseInput!) {
      createExpense(input: $input) {
        id
        category {
          id
          name
        }
        amount
        paymentMethod {
          bankName
        }
      }
    }
  `;

  const variables = {
    input: { data },
  };

  const createdExpenseData = await client.request(createExpense, variables);
  return createdExpenseData.createExpense as ICreateExpenseReturnType;
};

export interface IUpdateExpenseParam {
  amount: number;
  category: string;
  paymentType: EPaymentType;
  paymentMethod: string;
  note: string;
  attachments: CustomUploadedFile[];
}

export const updateExpense = async (id: string, data: IUpdateExpenseParam) => {
  const updateExpense = gql`
    mutation ($input: updateExpenseInput) {
      updateExpense(input: $input) {
        id
      }
    }
  `;

  const variables = {
    input: {
      where: { id },
      data,
    },
  };

  const updatedData = await client.request(updateExpense, variables);
  return updatedData;
};

export const importExpenses = async (rows: IExpenseImportRow[]) => {
  const importCSVExpense = gql`
    mutation ($input: importCSVExpenseInput!) {
      importCSVExpense(input: $input)
    }
  `;

  const variables = {
    input: { data: rows },
  };

  const importedData = await client.request(importCSVExpense, variables);
  return importedData.importCSVExpense as number;
};
